import { Component, Input, OnDestroy, ViewChild } from '@angular/core';
import { MediaAction } from '../../components/ag-media-gallery/ag-media-gallery.models';
import { AgentMediaComponentService } from './agent-media.service';
import { BehaviorSubject, map, Subscription } from 'rxjs';
import { AgMediaUploaderModalComponent } from '../ag-media-uploader/ag-media-uploader-modal.component';
import {
  AgentKeys,
  AGMedia,
  AgentFileDirectory,
  AgentFileDirectoryKeys,
  ChangeSourceType,
} from '@ag-common-lib/public-api';
import { confirm } from 'devextreme/ui/dialog';
import { ToastrService } from 'ngx-toastr';
import { AgentFileDirectoriesService } from '../../services/agent-files-directories.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AgentFilesMediaService } from '../../services/agent-files-media.service';
import { MediaUploaderTabs } from '../ag-media-uploader/ag-media-uploader-modal.models';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { FileDirectoryKeys } from '@ag-common-lib/lib/models/utils/file-directory.model';
import { AgentService } from '../../services/agent.service';

@UntilDestroy()
@Component({
  selector: 'ag-shr-agent-media',
  templateUrl: './agent-media.component.html',
  styleUrls: ['./agent-media.component.scss'],
  providers: [AgentMediaComponentService],
})
export class AgentMediaComponent implements OnDestroy {
  @ViewChild(AgMediaUploaderModalComponent, { static: false }) mediaUploaderComponent: AgMediaUploaderModalComponent;

  @Input() set agentId(agentId: string) {
    this.agentMediaComponentService.setAgentId(agentId);
    this.mediaPathPrefix$.next(`agents/${agentId}`);
    this.agentDbId = agentId;
    this.refreshDirectories(agentId);
  }
  @Input() canEdit = true;
  @Input() canDelete = true;
  @Input() canCreate = true;
  @Input() sourceType: ChangeSourceType | null = null;

  mediaPathPrefix$ = new BehaviorSubject<string>('');
  directoriesDataSource: AgentFileDirectory[];
  extraMediaActions: MediaAction[] = [
    // {
    //   icon: 'mdi mdi-flag-triangle',
    //   text: 'Set as Primary',
    //   actionFn: this.onSetPrimaryClicked.bind(this),
    // },
  ];

  selectedItemIndex: number | null = 0;
  folderName = '';

  protected readonly MediaUploaderTabs = MediaUploaderTabs;

  public agentDbId: string;

  private directoriesSubscription: Subscription | null = null;

  constructor(
    protected agentMediaComponentService: AgentMediaComponentService,
    private toastr: ToastrService,
    private agentFileDirectoriesService: AgentFileDirectoriesService,
    private agentFilesMediaService: AgentFilesMediaService,
    private agentService: AgentService,
  ) {}

  ngOnDestroy(): void {
    this.directoriesSubscription?.unsubscribe();
  }

  onSetPrimaryClicked(imageItem: AGMedia): void {
    this.agentMediaComponentService.setAsPrimaryImage(this.agentId, imageItem);
  }

  onDeleteClicked(imageItem: AGMedia): void {
    this.agentMediaComponentService.deleteMedia(this.agentDbId, imageItem);
  }

  itemClick(data): void {
    const item = data.itemData;
    this.agentMediaComponentService.setCurrentDirectory(item);
  }

  onItemClick(index: number): void {
    this.selectedItemIndex = index;
  }

  async uploadFileClicked(): Promise<void> {
    this.mediaUploaderComponent.showModal();
  }

  onMediaChanged(media: AGMedia): void {
    this.agentMediaComponentService.createMedia(this.agentDbId, media);
  }

  onDownloadMedia = async (paths: string[]) => {
    await this.agentService.updateAgentFields(this.agentDbId, {
      [AgentKeys.downloadedWasabiPaths]: paths,
    }, this.sourceType);
  };

  addFolder(): void {
    if (this.folderName) {
      const isCurrentFolderNameExist =
        this.directoriesDataSource?.filter(directory => directory.name == this.folderName).length > 0;
      if (isCurrentFolderNameExist) {
        this.toastr.error(`Folder Name "${this.folderName}" already exists`);
        return;
      }

      const directory: AgentFileDirectory = {
        [AgentFileDirectoryKeys.agentDbId]: this.agentDbId,
        [FileDirectoryKeys.name]: this.folderName,
        [FileDirectoryKeys.isSystem]: false,
      };

      this.agentMediaComponentService.createFolder(directory).then(() => {
        this.folderName = '';
        this.refreshDirectories(this.agentDbId);
      });
    }
  }

  async deleteFolder(folder: AgentFileDirectory): Promise<void> {
    const folderName = folder[FileDirectoryKeys.name] ?? '';
    const agentId = folder[AgentFileDirectoryKeys.agentDbId];

    await this.agentFilesMediaService
      .getList(agentId, folder[BaseModelKeys.dbId])
      .pipe(
        map(items => {
          if (items?.length) {
            this.toastr.error(`Folder "${folderName}" contains media. Please remove media before.`);
            return;
          }
          const result = confirm(`<i>Are you sure you want to Delete Folder "${folderName}"?</i>`, 'Confirm');
          result.then(dialogResult => {
            if (dialogResult) {
              this.agentMediaComponentService.deleteFolder(agentId, folder[BaseModelKeys.dbId]).then(() => {
                this.refreshDirectories(this.agentDbId);
              });
            }
          });
        }),
      )
      .subscribe();
  }

  private refreshDirectories(agentId: string): void {
    this.directoriesSubscription?.unsubscribe();
    this.directoriesDataSource = [];

    if (!agentId) {
      return;
    }

    this.directoriesSubscription = this.agentFileDirectoriesService
      .getList(agentId)
      .pipe(
        map(fileDirectories => {
          // Ignore late emissions from a previous agent after navigating away
          if (this.agentDbId !== agentId) {
            return;
          }

          this.directoriesDataSource = fileDirectories ?? [];
          if (!fileDirectories?.length) {
            this.agentMediaComponentService.setCurrentDirectory(null);
            return;
          }

          this.setDefaultDirectory(fileDirectories);
        }),
      )
      .subscribe();
  }

  private setDefaultDirectory(fileDirectories: AgentFileDirectory[]): void {
    const defaultIndex = 0;
    this.agentMediaComponentService.setCurrentDirectory(fileDirectories[defaultIndex]);
    this.selectedItemIndex = defaultIndex;
  }
}
